import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils/cn';
import {
  TrendingUp,
  TrendingDown,
  Repeat,
  Target,
  ArrowRightLeft,
  Layers,
  Shield,
  Clock,
  Zap,
  BarChart3,
  Grid3X3,
  ChevronRight,
  Star,
  Users,
  Play,
  Copy,
  ExternalLink,
  Search,
  Filter,
  Brain,
  Activity,
  Wallet,
  LineChart,
} from 'lucide-react';

interface Strategy {
  id: string;
  name: string;
  description: string;
  longDescription: string;
  icon: typeof TrendingUp;
  color: string;
  category: string;
  risk: 'low' | 'medium' | 'high';
  complexity: 'beginner' | 'intermediate' | 'advanced';
  protocols: string[];
  returns: string;
  users: number;
  rating: number;
  tags: string[];
  featured?: boolean;
}

const strategies: Strategy[] = [
  {
    id: 'grid-trading',
    name: 'Grid Trading Bot',
    description: 'Profit from market volatility with automated grid orders',
    longDescription: 'Places buy and sell orders at predetermined intervals around a set price, profiting from price oscillations in ranging markets.',
    icon: Grid3X3,
    color: '#00d4aa',
    category: 'market-making',
    risk: 'medium',
    complexity: 'beginner',
    protocols: ['Binance', 'Uniswap V3', 'dYdX'],
    returns: '+15-40% APY',
    users: 12500,
    rating: 4.8,
    tags: ['Automated', 'Range Trading', '24/7'],
    featured: true,
  },
  {
    id: 'dca-accumulation',
    name: 'DCA Accumulation',
    description: 'Dollar-cost average into positions over time',
    longDescription: 'Systematically buy assets at regular intervals regardless of price, reducing impact of volatility on overall purchase.',
    icon: TrendingUp,
    color: '#6366f1',
    category: 'accumulation',
    risk: 'low',
    complexity: 'beginner',
    protocols: ['Any CEX', 'Uniswap', '1inch', 'All EVM Chains'],
    returns: 'Market + reduced volatility',
    users: 28400,
    rating: 4.9,
    tags: ['Long-term', 'Low Risk', 'Multi-chain'],
    featured: true,
  },
  {
    id: 'dex-arbitrage',
    name: 'DEX Arbitrage Scanner',
    description: 'Exploit price differences across DEXs',
    longDescription: 'Monitor multiple DEXs for price discrepancies and execute atomic swaps to capture risk-free profits. Uses ChainOSINT for real-time pricing.',
    icon: ArrowRightLeft,
    color: '#8b5cf6',
    category: 'arbitrage',
    risk: 'medium',
    complexity: 'advanced',
    protocols: ['Uniswap', 'Sushiswap', 'Curve', 'PancakeSwap', 'QuickSwap'],
    returns: '+5-20% APY',
    users: 3200,
    rating: 4.5,
    tags: ['Atomic', 'MEV-Aware', 'Flash Loans'],
  },
  {
    id: 'cross-chain-arb',
    name: 'Cross-Chain Arbitrage',
    description: 'Profit from price differences across blockchains',
    longDescription: 'Monitor price discrepancies between ETH, BSC, Polygon, Arbitrum, and execute bridged trades. Uses our ChainOSINT engine for real-time multi-chain pricing.',
    icon: Repeat,
    color: '#ff007a',
    category: 'arbitrage',
    risk: 'high',
    complexity: 'advanced',
    protocols: ['ETH', 'BSC', 'Polygon', 'Arbitrum', 'Base'],
    returns: '+10-30% APY',
    users: 1850,
    rating: 4.3,
    tags: ['Multi-chain', 'Bridge', 'ChainOSINT'],
    featured: true,
  },
  {
    id: 'cex-dex-arb',
    name: 'CEX-DEX Spread Capture',
    description: 'Bridge price gaps between CEX and DEX',
    longDescription: 'Monitor price differences between Binance/Bybit spot markets and DEX pools. Execute customer trades at market rate while sourcing cheaper liquidity.',
    icon: Repeat,
    color: '#f0b90b',
    category: 'arbitrage',
    risk: 'high',
    complexity: 'advanced',
    protocols: ['Binance', 'Bybit', 'Uniswap', 'PancakeSwap'],
    returns: '+10-30% APY',
    users: 2450,
    rating: 4.4,
    tags: ['Cross-venue', 'Spread Capture', 'Internal Profit'],
  },
  {
    id: 'mean-reversion',
    name: 'Mean Reversion',
    description: 'Trade reversals when price deviates from average',
    longDescription: 'Uses Bollinger Bands and RSI to identify overbought/oversold conditions and trade the reversion to mean.',
    icon: Activity,
    color: '#f6465d',
    category: 'technical',
    risk: 'medium',
    complexity: 'intermediate',
    protocols: ['Any Exchange'],
    returns: '+20-50% APY',
    users: 8900,
    rating: 4.6,
    tags: ['Technical Analysis', 'Range Bound', 'Statistical'],
  },
  {
    id: 'momentum-trading',
    name: 'Momentum Trading',
    description: 'Ride strong trends with automated entries/exits',
    longDescription: 'Identifies and follows strong price momentum using moving averages and volume analysis across multiple timeframes.',
    icon: TrendingUp,
    color: '#00d4aa',
    category: 'technical',
    risk: 'high',
    complexity: 'intermediate',
    protocols: ['Any Exchange'],
    returns: '+30-100% APY',
    users: 6700,
    rating: 4.4,
    tags: ['Trend Following', 'High Reward', 'Multi-TF'],
  },
  {
    id: 'liquidity-provision',
    name: 'Concentrated LP Manager',
    description: 'Optimize Uniswap V3 concentrated liquidity positions',
    longDescription: 'Automatically manage LP positions, rebalance ranges based on price action, and compound fees. Handles impermanent loss by dynamic range adjustment.',
    icon: Layers,
    color: '#6366f1',
    category: 'yield',
    risk: 'medium',
    complexity: 'intermediate',
    protocols: ['Uniswap V3', 'PancakeSwap V3', 'Camelot'],
    returns: '+10-50% APY',
    users: 15600,
    rating: 4.7,
    tags: ['V3 LP', 'Auto-compound', 'IL Management'],
    featured: true,
  },
  {
    id: 'aave-leverage',
    name: 'Aave Leverage Farming',
    description: 'Recursive lending for amplified yields',
    longDescription: 'Supply collateral, borrow stables, swap and re-supply to create leveraged yield positions. Includes health factor monitoring and auto-deleveraging.',
    icon: Layers,
    color: '#B6509E',
    category: 'yield',
    risk: 'high',
    complexity: 'advanced',
    protocols: ['Aave V3', 'Compound'],
    returns: '+20-60% APY',
    users: 4200,
    rating: 4.3,
    tags: ['Leverage', 'Lending', 'Auto-deleverage'],
  },
  {
    id: 'rebalancing',
    name: 'Portfolio Rebalancer',
    description: 'Maintain target allocations automatically',
    longDescription: 'Monitors portfolio weights across CEX and DEX and automatically rebalances when allocations drift beyond threshold. Supports multi-chain portfolios.',
    icon: BarChart3,
    color: '#8b5cf6',
    category: 'portfolio',
    risk: 'low',
    complexity: 'beginner',
    protocols: ['Multi-chain', 'Any CEX'],
    returns: 'Market + lower risk',
    users: 21300,
    rating: 4.8,
    tags: ['Diversification', 'Multi-chain', 'Automated'],
  },
  {
    id: 'sentiment-trading',
    name: 'AI Sentiment Bot',
    description: 'Trade based on social media and news sentiment',
    longDescription: 'Uses NLP to analyze Twitter, Reddit, and news for sentiment shifts, executing trades on significant changes. Integrated with our AI model platform.',
    icon: Brain,
    color: '#f0b90b',
    category: 'ai',
    risk: 'high',
    complexity: 'advanced',
    protocols: ['Any Exchange'],
    returns: '+20-80% APY',
    users: 4500,
    rating: 4.2,
    tags: ['AI/ML', 'Social Data', 'Custom Models'],
  },
  {
    id: 'stop-loss-manager',
    name: 'Smart Stop Loss',
    description: 'Dynamic trailing stops with on-chain execution',
    longDescription: 'Implements trailing stop losses that execute on-chain via Gelato/Chainlink Automation, protecting positions even when offline.',
    icon: Shield,
    color: '#f6465d',
    category: 'risk',
    risk: 'low',
    complexity: 'beginner',
    protocols: ['Any DEX', 'Gelato', 'Chainlink'],
    returns: 'Risk Reduction',
    users: 32100,
    rating: 4.9,
    tags: ['Protection', 'Trailing', 'Trustless'],
  },
  {
    id: 'funding-rate-arb',
    name: 'Funding Rate Arbitrage',
    description: 'Capture funding payments on perpetuals',
    longDescription: 'Opens delta-neutral positions to collect funding rate payments when rates are elevated. Monitors rates across multiple perp DEXs.',
    icon: Target,
    color: '#00d4aa',
    category: 'arbitrage',
    risk: 'medium',
    complexity: 'advanced',
    protocols: ['Hyperliquid', 'dYdX', 'GMX', 'Vertex'],
    returns: '+15-40% APY',
    users: 2800,
    rating: 4.4,
    tags: ['Delta Neutral', 'Perpetuals', 'Cash & Carry'],
  },
  {
    id: 'mempool-watcher',
    name: 'Mempool Watcher',
    description: 'Monitor and react to pending transactions',
    longDescription: 'Watch mempool for large swaps, liquidity events, and whale movements. Execute front-run protection or copy trades (compliant strategies).',
    icon: Clock,
    color: '#8b5cf6',
    category: 'alpha',
    risk: 'high',
    complexity: 'advanced',
    protocols: ['Ethereum', 'BSC', 'ChainOSINT'],
    returns: 'Variable',
    users: 890,
    rating: 4.1,
    tags: ['Mempool', 'Real-time', 'Whale Watching'],
  },
  {
    id: 'hbx-staking',
    name: 'HBX Staking Optimizer',
    description: 'Maximize HBX staking rewards',
    longDescription: 'Automatically manage HBX token staking with optimal lock periods, compound rewards, and tier optimization for maximum fee discounts.',
    icon: Target,
    color: '#00d4aa',
    category: 'yield',
    risk: 'low',
    complexity: 'beginner',
    protocols: ['HybridX Platform'],
    returns: '+8-25% APY',
    users: 5600,
    rating: 4.8,
    tags: ['Native Token', 'Platform Rewards', 'Governance'],
  },
];

const categories = [
  { id: 'all', label: 'All Strategies', count: strategies.length },
  { id: 'market-making', label: 'Market Making', count: strategies.filter(s => s.category === 'market-making').length },
  { id: 'arbitrage', label: 'Arbitrage', count: strategies.filter(s => s.category === 'arbitrage').length },
  { id: 'technical', label: 'Technical', count: strategies.filter(s => s.category === 'technical').length },
  { id: 'yield', label: 'Yield & DeFi', count: strategies.filter(s => s.category === 'yield').length },
  { id: 'portfolio', label: 'Portfolio', count: strategies.filter(s => s.category === 'portfolio').length },
  { id: 'ai', label: 'AI/ML', count: strategies.filter(s => s.category === 'ai').length },
  { id: 'alpha', label: 'Alpha Strategies', count: strategies.filter(s => s.category === 'alpha').length },
  { id: 'risk', label: 'Risk Management', count: strategies.filter(s => s.category === 'risk').length },
];

export default function Strategies() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(null);

  const filteredStrategies = strategies.filter(s => {
    const matchesCategory = activeCategory === 'all' || s.category === activeCategory;
    const matchesSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const featuredStrategies = strategies.filter(s => s.featured);

  const useStrategy = (strategy: Strategy) => {
    navigate(`/workflow/new?template=${strategy.id}`);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold text-text-primary mb-2">Strategy Templates</h1>
        <p className="text-text-secondary">
          Pre-built trading bot strategies powered by smart contracts, oracles, and ChainOSINT
        </p>
      </div>

      {/* Featured */}
      <div className="mb-8">
        <h2 className="font-heading text-lg font-semibold text-text-primary mb-4">Featured Strategies</h2>
        <div className="grid grid-cols-3 gap-4">
          {featuredStrategies.map((strategy) => {
            const Icon = strategy.icon;
            return (
              <button
                key={strategy.id}
                onClick={() => setSelectedStrategy(strategy)}
                className="card p-5 text-left hover:border-primary/30 transition-all group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${strategy.color}15` }}
                  >
                    <Icon className="h-6 w-6" style={{ color: strategy.color }} />
                  </div>
                  <div className="flex items-center gap-1 text-primary">
                    <Star className="h-4 w-4 fill-current" />
                    <span className="text-sm font-medium">{strategy.rating}</span>
                  </div>
                </div>
                <h3 className="font-semibold text-text-primary mb-1 group-hover:text-primary transition-colors">
                  {strategy.name}
                </h3>
                <p className="text-sm text-text-secondary mb-3">{strategy.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-xs text-text-tertiary">
                    <Users className="h-3.5 w-3.5" />
                    <span>{strategy.users.toLocaleString()} users</span>
                  </div>
                  <span className="text-xs font-medium text-success">{strategy.returns}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
          <input
            type="text"
            placeholder="Search strategies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={cn(
                "px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors",
                activeCategory === cat.id
                  ? "bg-primary/10 text-primary"
                  : "text-text-secondary hover:text-text-primary hover:bg-white/[0.04]"
              )}
            >
              {cat.label}
              <span className="ml-1.5 text-xs opacity-60">{cat.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Strategy Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredStrategies.map((strategy) => {
          const Icon = strategy.icon;
          return (
            <div
              key={strategy.id}
              className="card p-4 hover:border-white/[0.15] transition-all cursor-pointer"
              onClick={() => setSelectedStrategy(strategy)}
            >
              <div className="flex items-start gap-3 mb-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${strategy.color}15` }}
                >
                  <Icon className="h-5 w-5" style={{ color: strategy.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-text-primary truncate">{strategy.name}</h3>
                  <p className="text-xs text-text-secondary line-clamp-2">{strategy.description}</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-3">
                  <span className={cn(
                    "px-2 py-0.5 rounded-full",
                    strategy.risk === 'low' ? "bg-success/10 text-success" :
                    strategy.risk === 'medium' ? "bg-warning/10 text-warning" :
                    "bg-danger/10 text-danger"
                  )}>
                    {strategy.risk} risk
                  </span>
                  <span className="text-text-tertiary">{strategy.complexity}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-text-tertiary" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Strategy Detail Modal */}
      {selectedStrategy && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6" onClick={() => setSelectedStrategy(null)}>
          <div className="bg-background-secondary rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/[0.06]" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-white/[0.06]">
              <div className="flex items-start gap-4">
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${selectedStrategy.color}15` }}
                >
                  <selectedStrategy.icon className="h-7 w-7" style={{ color: selectedStrategy.color }} />
                </div>
                <div className="flex-1">
                  <h2 className="font-heading text-xl font-bold text-text-primary mb-1">{selectedStrategy.name}</h2>
                  <p className="text-text-secondary">{selectedStrategy.description}</p>
                </div>
                <button onClick={() => setSelectedStrategy(null)} className="p-2 hover:bg-white/[0.06] rounded-lg">
                  <Zap className="h-5 w-5 text-text-tertiary rotate-45" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-sm font-medium text-text-primary mb-2">How It Works</h3>
                <p className="text-text-secondary text-sm leading-relaxed">{selectedStrategy.longDescription}</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-background-tertiary rounded-xl p-4">
                  <div className="text-xs text-text-tertiary mb-1">Expected Returns</div>
                  <div className="text-lg font-semibold text-success">{selectedStrategy.returns}</div>
                </div>
                <div className="bg-background-tertiary rounded-xl p-4">
                  <div className="text-xs text-text-tertiary mb-1">Risk Level</div>
                  <div className={cn(
                    "text-lg font-semibold capitalize",
                    selectedStrategy.risk === 'low' ? "text-success" :
                    selectedStrategy.risk === 'medium' ? "text-warning" :
                    "text-danger"
                  )}>{selectedStrategy.risk}</div>
                </div>
                <div className="bg-background-tertiary rounded-xl p-4">
                  <div className="text-xs text-text-tertiary mb-1">Active Users</div>
                  <div className="text-lg font-semibold text-text-primary">{selectedStrategy.users.toLocaleString()}</div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-text-primary mb-2">Supported Protocols</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedStrategy.protocols.map((p) => (
                    <span key={p} className="px-3 py-1.5 bg-background-tertiary rounded-lg text-sm text-text-primary">{p}</span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-text-primary mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedStrategy.tags.map((t) => (
                    <span key={t} className="px-2 py-1 bg-primary/10 rounded text-xs text-primary">{t}</span>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
                <button
                  onClick={() => useStrategy(selectedStrategy)}
                  className="flex-1 btn-primary btn-lg"
                >
                  <Play className="h-4 w-4" />
                  Use This Strategy
                </button>
                <button className="btn-secondary btn-lg">
                  <Copy className="h-4 w-4" />
                  Clone & Customize
                </button>
                <button className="btn-ghost btn-lg">
                  <ExternalLink className="h-4 w-4" />
                  Docs
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
